import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import {
  createAdvanceSalaryBatch,
  fetchAdvanceSalaryFormMeta,
  periodKey,
  periodLabel,
  salaryAdvancesApiErrorMessage,
  type AdvanceSalaryFormMeta,
} from "@/lib/hr/salaryAdvancesStore";
import { cn } from "@/lib/utils";
import { CalendarRange, ChevronsLeft, ChevronsRight, HandCoins, Loader2, Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
] as const;

type DraftLine = {
  clientId: string;
  advanceAmount: string;
  periodYear: number;
  periodMonth: number;
  reason: string;
};

function newClientId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `line-${Date.now()}-${Math.random()}`;
}

function emptyLine(): DraftLine {
  return { clientId: newClientId(), advanceAmount: "", periodYear: 0, periodMonth: 0, reason: "" };
}

function formatMoney(value: string): string {
  const raw = value.replace(/[^\d.]/g, "");
  if (!raw) return value;
  const [whole, ...rest] = raw.split(".");
  const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
  return rest.length ? `${formatted}.${rest.join("")}` : formatted;
}

function safeNumber(v: string): number | null {
  const n = Number(v.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function buildPeriodOptions(
  meta: AdvanceSalaryFormMeta | null,
  selectedKeys: Set<string>,
  currentLineKey?: string,
): Array<{ year: number; month: number; key: string; label: string }> {
  const blocked = new Set((meta?.blockedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const reserved = new Set((meta?.reservedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const now = new Date();
  const options: Array<{ year: number; month: number; key: string; label: string }> = [];
  const start = new Date(now.getFullYear(), 0, 1);
  for (let offset = 0; offset < 24; offset += 1) {
    const d = new Date(start.getFullYear(), start.getMonth() + offset, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = periodKey(year, month);
    if (blocked.has(key)) continue;
    if (reserved.has(key)) continue;
    if (selectedKeys.has(key) && key !== currentLineKey) continue;
    options.push({ year, month, key, label: periodLabel(year, month, MONTHS) });
  }
  return options;
}

function SalaryPeriodPicker({
  open,
  onOpenChange,
  periodYear,
  periodMonth,
  periodOptions,
  pickerYear,
  onPickerYearChange,
  onSelect,
  disabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodYear: number;
  periodMonth: number;
  periodOptions: Array<{ year: number; month: number; key: string; label: string }>;
  pickerYear: number;
  onPickerYearChange: (year: number) => void;
  onSelect: (year: number, month: number) => void;
  disabled?: boolean;
}) {
  const availableKeys = new Set(periodOptions.map((opt) => opt.key));
  const minPickerYear = periodOptions[0]?.year ?? new Date().getFullYear();
  const maxPickerYear = periodOptions[periodOptions.length - 1]?.year ?? minPickerYear;
  const selectedLabel =
    periodYear && periodMonth ? periodLabel(periodYear, periodMonth, MONTHS) : null;

  return (
    <Popover modal={true} open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "glass-input h-10 w-full justify-start text-left font-normal",
            !selectedLabel && "text-muted-foreground",
          )}
          disabled={disabled}
        >
          <CalendarRange className="mr-2 h-4 w-4 shrink-0 opacity-70" />
          {selectedLabel ?? "Select period"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[100] w-[min(100vw-2rem,20rem)] p-4" align="start" sideOffset={6}>
        <div className="mb-3 space-y-1">
          <p className="text-sm font-semibold text-foreground">Select salary period</p>
          <p className="text-xs text-muted-foreground">Select month and year for this advance.</p>
        </div>
        {periodOptions.length === 0 ? (
          <p className="rounded-lg border border-border/50 bg-muted/10 p-3 text-center text-xs text-muted-foreground">
            No available salary periods for this employee.
          </p>
        ) : (
          <div className="rounded-lg border border-border/50 bg-muted/10 p-3">
            <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/50 pb-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={pickerYear <= minPickerYear}
                onClick={() => onPickerYearChange(Math.max(minPickerYear, pickerYear - 1))}
                aria-label="Previous year"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold tabular-nums text-foreground">{pickerYear}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={pickerYear >= maxPickerYear}
                onClick={() => onPickerYearChange(Math.min(maxPickerYear, pickerYear + 1))}
                aria-label="Next year"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MONTH_LABELS.map((label, monthIndex) => {
                const month = monthIndex + 1;
                const key = periodKey(pickerYear, month);
                const isAvailable = availableKeys.has(key);
                const isSelected = periodYear === pickerYear && periodMonth === month;
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => onSelect(pickerYear, month)}
                    className={cn(
                      "h-10 rounded-md text-sm font-medium transition-colors",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : isAvailable
                          ? "border border-border/50 bg-background text-foreground hover:bg-muted"
                          : "cursor-not-allowed border border-transparent bg-muted/30 text-muted-foreground/50",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Selected: <span className="font-medium text-foreground">{selectedLabel ?? "None"}</span>
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export type SalaryAdvanceCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  onSaved?: () => void;
};

export function SalaryAdvanceCreateDialog({ open, onOpenChange, employeeId, onSaved }: SalaryAdvanceCreateDialogProps) {
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [formMeta, setFormMeta] = useState<AdvanceSalaryFormMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [periodPickerLineId, setPeriodPickerLineId] = useState<string | null>(null);
  const [periodPickerYear, setPeriodPickerYear] = useState(() => new Date().getFullYear());

  useEffect(() => {
    if (!open) return;
    setLines([emptyLine()]);
    setFormMeta(null);
    setPeriodPickerLineId(null);
  }, [open, employeeId]);

  useEffect(() => {
    if (!open || !employeeId) {
      setFormMeta(null);
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    void fetchAdvanceSalaryFormMeta(employeeId)
      .then((meta) => {
        if (!cancelled) setFormMeta(meta);
      })
      .catch((e) => {
        if (!cancelled) {
          setFormMeta(null);
          toast.error(salaryAdvancesApiErrorMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, employeeId]);

  const selectedPeriodKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const line of lines) {
      if (line.periodYear && line.periodMonth) keys.add(periodKey(line.periodYear, line.periodMonth));
    }
    return keys;
  }, [lines]);

  const updateLine = (clientId: string, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((line) => (line.clientId === clientId ? { ...line, ...patch } : line)));
  };

  const close = () => onOpenChange(false);

  const save = async () => {
    if (saving || !employeeId) return;
    if (!formMeta) return toast.error("Employee salary details are still loading.");

    const payloadLines: Array<{ advanceAmount: number; periodYear: number; periodMonth: number; reason: string | null }> = [];
    const seen = new Set<string>();

    for (const line of lines) {
      const amount = safeNumber(line.advanceAmount);
      if (!amount || amount <= 0) return toast.error("Each advance amount must be greater than zero.");
      if (!line.periodYear || !line.periodMonth) return toast.error("Select a salary period for each advance line.");
      if (amount > formMeta.grossSalary) {
        return toast.error(`Advance amount cannot exceed gross salary (${formatAmountCommas(String(formMeta.grossSalary))}).`);
      }
      const key = periodKey(line.periodYear, line.periodMonth);
      if (seen.has(key)) return toast.error("Each salary period can only be used once.");
      seen.add(key);
      payloadLines.push({
        advanceAmount: amount,
        periodYear: line.periodYear,
        periodMonth: line.periodMonth,
        reason: line.reason.trim() || null,
      });
    }

    setSaving(true);
    try {
      const result = await createAdvanceSalaryBatch({ employeeId, lines: payloadLines });
      toast.success(result.message);
      close();
      onSaved?.();
    } catch (e) {
      toast.error(salaryAdvancesApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">Record Salary Advance</DialogTitle>
              <p className="text-xs text-muted-foreground">
                Issue salary advances against upcoming monthly payroll cycles.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Gross Salary Limit & Live Calculation Card */}
          <div className="rounded-xl border border-border/60 bg-muted/10 p-4 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/40 pb-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-bold text-foreground">Monthly Salary Cap:</span>
                {metaLoading ? (
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading limit…
                  </span>
                ) : formMeta ? (
                  <span className="font-extrabold text-primary text-sm">
                    {formatAmountCommas(String(formMeta.grossSalary))} PKR
                  </span>
                ) : (
                  <span className="text-destructive font-medium">Unable to fetch limit</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Finalized or paid payroll periods are excluded.
              </p>
            </div>

            {/* Quick Percentage Presets */}
            {formMeta?.grossSalary ? (
              <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                <span className="text-[11px] font-semibold text-muted-foreground">Quick Amount:</span>
                {[0.25, 0.5, 0.75, 1.0].map((ratio) => {
                  const pctLabel = `${Math.round(ratio * 100)}%`;
                  const calcAmt = Math.round(formMeta.grossSalary * ratio);
                  return (
                    <button
                      key={ratio}
                      type="button"
                      onClick={() => {
                        if (lines.length > 0) {
                          updateLine(lines[0].clientId, { advanceAmount: formatAmountCommas(String(calcAmt)) });
                        }
                      }}
                      className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors flex items-center gap-1"
                    >
                      <span className="text-primary font-bold">{pctLabel}</span>
                      <span className="text-muted-foreground text-[10px]">({formatAmountCommas(String(calcAmt))})</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="hidden md:grid md:grid-cols-[minmax(9rem,1fr)_minmax(11rem,1.1fr)_minmax(12rem,1.4fr)_auto] gap-3 px-1">
              <Label className="text-xs font-bold text-foreground">Advance Amount (PKR)</Label>
              <Label className="text-xs font-bold text-foreground">Salary Period Target</Label>
              <Label className="text-xs font-bold text-foreground">Reason / Note</Label>
              <span className="sr-only">Actions</span>
            </div>

            {lines.map((line, index) => {
              const lineKey =
                line.periodYear && line.periodMonth ? periodKey(line.periodYear, line.periodMonth) : undefined;
              const periodOptions = buildPeriodOptions(formMeta, selectedPeriodKeys, lineKey);

              return (
                <div
                  key={line.clientId}
                  className="grid grid-cols-1 md:grid-cols-[minmax(9rem,1fr)_minmax(11rem,1.1fr)_minmax(12rem,1.4fr)_auto] gap-3 items-end rounded-xl border border-border/60 p-3 md:border-0 md:p-0 md:rounded-none bg-background/50"
                >
                  <div className="space-y-2">
                    {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Advance amount</Label> : null}
                    <div className="relative">
                      <Input
                        className="glass-input h-10 pr-12 font-semibold"
                        value={line.advanceAmount}
                        onChange={(e) => updateLine(line.clientId, { advanceAmount: formatMoney(e.target.value) })}
                        placeholder="e.g. 10,000"
                        inputMode="decimal"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                        PKR
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Salary period</Label> : null}
                    <SalaryPeriodPicker
                      open={periodPickerLineId === line.clientId}
                      onOpenChange={(pickerOpen) => {
                        if (pickerOpen) {
                          setPeriodPickerLineId(line.clientId);
                          setPeriodPickerYear(line.periodYear || periodOptions[0]?.year || new Date().getFullYear());
                        } else {
                          setPeriodPickerLineId((id) => (id === line.clientId ? null : id));
                        }
                      }}
                      periodYear={line.periodYear}
                      periodMonth={line.periodMonth}
                      periodOptions={periodOptions}
                      pickerYear={periodPickerYear}
                      onPickerYearChange={setPeriodPickerYear}
                      onSelect={(year, month) => {
                        updateLine(line.clientId, { periodYear: year, periodMonth: month });
                        setPeriodPickerLineId(null);
                      }}
                      disabled={metaLoading || !formMeta}
                    />
                  </div>
                  <div className="space-y-2">
                    {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Reason</Label> : null}
                    <Input
                      className="glass-input h-10"
                      value={line.reason}
                      onChange={(e) => updateLine(line.clientId, { reason: e.target.value })}
                      placeholder="e.g. Emergency advance request"
                    />
                  </div>
                  <div className="flex items-center gap-2 pb-0.5">
                    {index === lines.length - 1 ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0 border-primary/20 bg-primary/10 text-primary hover:bg-primary/20"
                        onClick={() => setLines((prev) => [...prev, emptyLine()])}
                        aria-label="Add another period"
                        title="Add another advance period line"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0 text-destructive border-destructive/20 hover:bg-destructive/10"
                        onClick={() =>
                          setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.clientId !== line.clientId)))
                        }
                        aria-label="Remove line"
                        title="Remove line"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter className="pt-4 border-t border-border/50 gap-2">
          <Button variant="outline" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || !employeeId || metaLoading}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Record Advance Salary
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
